/**
 * **Edit retainer** — `mockups/studio/client-detail.html` §Retainer's head button.
 *
 * It proposes; it does not apply. `retainers_change_package$` writes a
 * `RET-CHG-NNN` the client has to approve, and `applyDueChanges` writes it onto the
 * retainer on its own billing day (session 12) — so the confirmation says what is
 * being *sent*, and the effective date is a billing day the reader chooses rather
 * than "now".
 *
 * `retainers_change_package` is on `actionFields.ts`'s `HAND_WRITTEN` list, naming
 * `PackageBuilder` as the component that owns its form. That is exactly what this
 * is: `PackageBuilder` with `current` set, which is what makes the answer a
 * `package_change` price with a before, an after and a monthly delta.
 *
 * **The delta is the engine's, not this dialog's.** `amount` on that action is "the
 * **difference** it makes to the monthly amount … refused when it is not what the
 * change comes to", so it is `computed.change.delta` and nothing else — a builder
 * that subtracted two totals in the browser would be refused the first time a
 * package crossed a bundle band.
 *
 * **Custom lines are carried, not composed.** `retainers_change_package` takes the
 * whole package after the change in two lists, and a line left out of `custom_lines`
 * is a line the change drops — so the retainer's own lines are read off its items and
 * sent back verbatim, and they are shown read-only under the blocks so whoever
 * presses *Send it to them* can see what they are re-committing. Re-pricing one is
 * `retainers_change_package` with a different `custom_lines`, and this dialog does not
 * offer it: an editor for an amount agreed in a contract is a decision with an email
 * behind it, not a text box beside a block counter.
 */

import { useState } from 'react'

import {
  firstDayOf,
  nextPeriod,
  periodOf,
  type ComputedPrice,
  type Currency,
  type PackageItemInput,
  type RetainerView,
  type TierCode,
  DateOnlySchema,
} from '@fareast/shared'
import { z } from 'zod'

import { messageOf } from '../../../api/problem.js'
import { catalogItemsOf, customLinesOf } from '../../../components/client/customLines.js'
import { reviewOf, useStudioAction, useStudioRead } from '../../../api/studio.js'
import { Button } from '../../../components/portal/Button.js'
import { Input, Textarea, useZodForm } from '../../../components/portal/Form.js'
import { Stack } from '../../../components/portal/Layout.js'
import { ConfirmDialog, Dialog } from '../../../components/portal/Overlay.js'
import { Notice } from '../../../components/portal/Status.js'
import { Prose } from '../../../components/portal/Text.js'
import { PackageBuilder, type PackageService } from '../../../components/studio/PackageBuilder.js'
import { NeedsReviewNotice } from '../../../components/studio/NeedsReviewNotice.js'
import { confirmLabel } from '../../../components/studio/ActionForm.js'
import { useFormat } from '../../../format/useFormat.js'
import { now } from '../../../integrations/clock.js'

/** What `catalog_list_services` answers with, narrowed to what the builder reads. */
const ServicesSchema = z.object({
  services: z.array(
    z.object({
      code: z.string(),
      name: z.string(),
      category: z.string(),
      block_scope: z.string(),
      quote_only: z.boolean(),
      active: z.boolean(),
      replaces_service: z.string().nullable(),
    }),
  ),
})

const WordsSchema = z.object({
  /*
   * `DateOnlySchema`, not a regex of the same shape. This was the only hand-written
   * date pattern in `apps/web/src`, and it accepted `2026-02-31`: the shared schema
   * refines on `isDateOnly`, which asks whether the day exists. The column it feeds is
   * the day a package change takes effect.
   */
  effective_on: DateOnlySchema,
  note: z.string(),
})

/*
 * Two types, not one. `DateOnlySchema` takes a `string` and answers a branded
 * `DateOnly`, so what the text box holds and what the submit handler receives are
 * different types — which is the whole point of the brand and is what `useZodForm`'s
 * two parameters are for.
 */
type WordsIn = z.input<typeof WordsSchema>
type Words = z.output<typeof WordsSchema>

export type EditRetainerDialogProps = {
  readonly client: string
  readonly retainer: RetainerView
  readonly tier: TierCode
  readonly currency: Currency
}

export function EditRetainerDialog({ client, retainer, tier, currency }: EditRetainerDialogProps) {
  const format = useFormat()
  const [open, setOpen] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)

  /*
   * **Blocks here, custom lines beside them — and both are sent.**
   *
   * `retainer.items` is one list of two kinds (`RetainerItemView.kind`), and this
   * built `current` out of all of it: a custom row has a `null` service, so the
   * proposal carried a block with no code — and, worse, `retainers_change_package`'s
   * `custom_lines` is "the whole set after the change", so sending none of them
   * **drops every custom line the client has** on a change that was meant to move one
   * block. The client would be approving a package that quietly lost the work they
   * are paying for.
   *
   * So the builder composes the blocks and `lines` is sent verbatim. `amount` on a
   * custom row is already the agreed amount in the billing currency, which is exactly
   * what `CustomLineSchema` takes — no conversion, and nothing to re-derive.
   */
  const current: readonly PackageItemInput[] = catalogItemsOf(retainer.items).map((item) => ({
    service: item.service,
    blocks: item.blocks,
  }))
  const lines = customLinesOf(retainer.items)
  const [items, setItems] = useState<readonly PackageItemInput[]>(current)
  const [priced, setPriced] = useState<ComputedPrice | null>(null)

  const form = useZodForm<WordsIn, Words>(WordsSchema, {
    defaultValues: { effective_on: nextBillingDay(), note: '' },
  })

  /*
   * The card, read only while the dialog is open. It is a quarter of a megabyte and
   * nobody who never presses this button should pay for it.
   */
  const catalog = useStudioRead('catalog_list_services', {}, { enabled: open, inline: true })
  const services: readonly PackageService[] =
    catalog.data === undefined ? [] : ServicesSchema.parse(catalog.data).services

  const change = useStudioAction('retainers_change_package')
  const review = reviewOf(change.data)
  const delta = priced?.change?.delta ?? null
  /*
   * What the box holds, watched — the sentence in the confirm dialog names the day,
   * and it has to be the day the reader typed rather than the one the form was
   * opened with. It is unbranded here on purpose: nothing has parsed it yet, and
   * `format.day` reads a `YYYY-MM-DD` string.
   */
  const effectiveOn = form.watch('effective_on')

  const send = async (words: Words) => {
    if (delta === null) return
    setFailure(null)
    try {
      const answer = await change.mutateAsync({
        client,
        /*
         * The widget allowances ride along. `PackageItemInput` is `{ service,
         * blocks }` and `retainers_change_package`'s own item takes an
         * `included_widget_qty` as well — a change that dropped it would silently
         * un-include the widget units a package already covers.
         */
        items: items.map((item) => ({
          service: item.service,
          blocks: item.blocks,
          included_widget_qty:
            retainer.items.find((one) => one.service === item.service)?.included_widget_qty ?? [],
        })),
        /*
         * The whole set after the change, and this dialog changes none of them — so
         * it is the retainer's own lines, sent back unchanged. A line left out here
         * is a line dropped (`ProposeRetainerChangeSchema`).
         */
        custom_lines: lines.map((line) => ({ ...line })),
        effective_on: words.effective_on,
        note: words.note,
        amount: delta,
      })
      setConfirming(false)
      if (reviewOf(answer) === null) setOpen(false)
    } catch (error: unknown) {
      setFailure(messageOf(error))
    }
  }

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next)
          if (!next) setFailure(null)
        }}
        title="Propose a package change"
        trigger={<Button size="sm">Edit retainer</Button>}
      >
        <Stack>
          <Prose>
            Nothing here changes the retainer. It writes a package change the client has to approve,
            and it is applied on the billing day it names — never mid-period.
          </Prose>

          {catalog.isError ? (
            <Notice need role="alert">
              We could not read the rate card, so the blocks cannot be drawn.
            </Notice>
          ) : (
            <PackageBuilder
              services={services}
              tier={tier}
              currency={currency}
              value={items}
              onChange={setItems}
              current={current}
              customLines={lines}
              onPriced={setPriced}
            />
          )}

          <Input
            control={form.control}
            name="effective_on"
            label="Effective on"
            hint="The billing day it takes effect — the 1st of a month, and not one that has passed."
          />
          <Textarea
            control={form.control}
            name="note"
            label="What to tell them"
            hint="The break-even sentence they read beside the numbers. Yours to write."
          />

          {review === null ? null : <NeedsReviewNotice review={review} />}
          {failure === null ? null : (
            <Notice need role="alert">
              {failure}
            </Notice>
          )}

          <Button
            variant="primary"
            disabled={delta === null || change.isPending}
            onClick={() => {
              void form.handleSubmit(() => {
                setConfirming(true)
              })()
            }}
          >
            {change.isPending ? 'Working…' : 'Send it to them'}
          </Button>
        </Stack>
      </Dialog>

      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Send this package change"
        confirmLabel={confirmLabel(
          'Send',
          delta === null ? null : `${format.money(delta)} a month`,
          client,
        )}
        cancelLabel="Keep editing"
        busy={change.isPending}
        onConfirm={() => {
          /*
           * `handleSubmit`, not `getValues`. The values in the box are the box's —
           * unvalidated, and unbranded — and this used to hand them straight to the
           * action: a day that does not exist reached the API as a 400 with no field
           * error on the screen that caused it. react-hook-form parses, puts the
           * message under the input, and calls `send` only with what came out.
           */
          void form.handleSubmit(send)()
        }}
      >
        <Prose>
          This commits {delta === null ? 'nothing yet' : `${format.money(delta)} a month`} against{' '}
          {client}, from {format.day(effectiveOn)}, once they approve it. They can decline it, and
          the current package bills as it does today until the day it takes effect.
        </Prose>
      </ConfirmDialog>
    </>
  )
}

/** The first of next month — the earliest billing day a change may name. */
function nextBillingDay(): string {
  return firstDayOf(nextPeriod(periodOf(now())))
}
